import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useNotification } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import type { QuestionOption } from '@aphp-ema/common/questionnaire';
import { useParseQuestionOptionsCsv } from '@/features/questionnaires/hooks/questionnaire/useParseQuestionOptionsCsv';

type OptionsCsvUploaderProps = {
  onOptionsUploaded: (options: QuestionOption[]) => void;
  disabled?: boolean;
};

export const OptionsCsvUploader: React.FC<OptionsCsvUploaderProps> = ({
  onOptionsUploaded,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { open: openNotification } = useNotification();
  const { parseOptionsCsv, isLoading } = useParseQuestionOptionsCsv();

  const handleCsvUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      openNotification?.({
        type: 'error',
        message: t('pages.studies.questions.options.errors.notCsvFile'),
      });
      return;
    }

    const newOptions = await parseOptionsCsv(file);
    onOptionsUploaded(newOptions);

    // Reset the input
    event.target.value = '';
  };

  return (
    <Button
      variant="outlined"
      component="label"
      startIcon={isLoading ? <CircularProgress size={16} /> : <CloudUpload />}
      disabled={disabled || isLoading}
      size="small"
    >
      {isLoading
        ? t('pages.studies.questions.options.uploading')
        : t('pages.studies.questions.options.uploadCsv')}
      <input
        type="file"
        hidden
        accept=".csv"
        onChange={handleCsvUpload}
        disabled={disabled || isLoading}
      />
    </Button>
  );
};
